import { useTheme, Box, Typography } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { useState } from 'react';
import ExoTable from '../../../components/exo/ExoTable';
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from '../../../components/exo/ExoTableToolbar';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { useTranslation } from 'react-i18next';
import DateLabel from '../../../components/label/DateLabel';
import MoneyLabel from '../../../components/label/MoneyLabel';
import ExoDialog from '../../../components/exo/ExoDialog';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import useFetch from '../../../api/useFetch';
import { useNavigate } from 'react-router-dom';
import ExtendPeriodicExpense from './ExtendPeriodicExpense';
import NewPeriodicExpense from '../../../components/new/NewPeriodicExpense';

export const PeriodicExpensesTab = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { apiData, setApiData } = useFetch('periodic-expenses'); //change api here

  const [newPeriodicExpense, setNewPeriodicExpense] = useState(false);

  const Columns = [
    {
      header: <RequestPageIcon />, // header of column
      key: 'file', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      width: '33px', // width of column
      hiddenMobile: true,
      cell: ({ row }) => <Box>{row.file && <RequestPageIcon />}</Box>,
    },
    {
      header: t('Title'), // header of column
      key: 'title', // key of column in row-object
      thAlign: 'left', // header text alignment
      tdAlign: 'left', // cell text alignment
      sortable: true, // enable sort function for column
      hiddenMobile: false, // hides the column on mobile
      onClick: (row) => navigate(`/expenses/periodic-expenses/${row.id}`),
    },
    {
      header: t('Start Date'), // header of column
      key: 'startDate', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      sortable: true, // enable sort function for column
      width: '150px', // width of column
      cell: ({ row }) => (
        <Box className="monospace">
          <DateLabel date={row.startDate} />
        </Box>
      ), // custom xml for cells in the column. you get the row as object
      hiddenMobile: true, // hides the column on mobile
    },
    {
      header: t('End Date'), // header of column
      key: 'endDate', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      sortable: true, // enable sort function for column
      width: '150px', // width of column
      cell: ({ row }) => (
        <Box className="monospace">
          <DateLabel date={row.endDate} />
        </Box>
      ), // custom xml for cells in the column. you get the row as object
      hiddenMobile: true, // hides the column on mobile
    },
    {
      header: t('Costs'), // header of column
      key: 'amount', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      sortable: true, // enable sort function for column
      width: '140px', // width of column
      hiddenMobile: true,
      cell: ({ row }) => (
        <Box className="monospace">
          <MoneyLabel money={row.amount} />
        </Box>
      ), // custom xml for cells in the column. you get the row as object
    },
    {
      header: t('Total Amount'), // header of column
      key: 'openAmount', // key of column in row-object
      thAlign: 'right', // header text alignment
      tdAlign: 'right', // cell text alignment
      sortable: true, // enable sort function for column
      width: '140px', // width of column
      cell: ({ row }) => (
        <Typography
          className="monospace"
          color={
            Math.round(parseFloat(row.openAmount) * 100) / 100 > 0
              ? colors.error
              : colors.success
          }
          fontWeight={700}
        >
          {Math.round(parseFloat(row.openAmount) * 100) / 100 === 0 ? (
            <PriceCheckIcon />
          ) : (
            <MoneyLabel money={row.openAmount} />
          )}
        </Typography>
      ), // custom xml for cells in the column. you get the row as object
    },
  ];

  // actions
  const [periodicExpenseToEdit, setPeriodicExpenseToEdit] = useState(null);
  function handleClickEdit(data) {
    setPeriodicExpenseToEdit({
      ...data,
      periodicExpense: {
        amount: data.amount,
        position: data.position,
        taxAssignment: data.taxAssignment,
        taxRate: data.taxRate,
        costCenter: data.costCenter,
        note: data.note,
      },
    });
    setNewPeriodicExpense(true);
  }

  function handleCreatePeriodicExpense(newPeriodicExpense) {
    setApiData(updateOrCreate(apiData, newPeriodicExpense));
    setNewPeriodicExpense(false);
  }

  return (
    <>
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t('New Periodic Expense')}
          icon={<NoteAddIcon />}
          onClick={() => {
            setPeriodicExpenseToEdit(null);
            setNewPeriodicExpense(true);
          }}
        />
      </ExoTableToolbar>
      <ExoTable
        data={apiData}
        isLoading={!Boolean(apiData)}
        columns={Columns}
        extendElement={({ row, onUpdate, onDelete }) => (
          <ExtendPeriodicExpense
            row={row}
            onUpdate={onUpdate}
            onClickEdit={handleClickEdit}
            onDelete={onDelete}
          />
        )}
      />

      <ExoDialog open={newPeriodicExpense}>
        <NewPeriodicExpense
          onCreate={handleCreatePeriodicExpense}
          onClose={() => setNewPeriodicExpense(false)}
          periodicExpense={periodicExpenseToEdit}
          open={newPeriodicExpense}
        />
      </ExoDialog>
    </>
  );
};
